import { ConnectorStatus } from "@/components/connectors/ConnectorStatus";
import { connectorLoadState } from "@/stores/connectors/loading";
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Upload } from "tus-js-client";
import { UploadCloud } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { capture } from "@/lib/sentry/capture";
import { AuthPageHeader, AuthPageLayout } from "@/components/auth/AuthPageLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  connectorOrigin,
  finishConnectorUpload,
  loadConnectorUpload,
  transferUrl,
} from "@/stores/connectors/repository";

function reportTransferFailure() {
  capture(new Error("Connector file transfer failed"), {
    tags: { boundary: "connector_upload" },
    fingerprint: ["connector-upload"],
    context: {},
  });
}

function useFileTransfer(id: string) {
  const { userId } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [settling, setSettling] = useState(false);
  const [now, setNow] = useState(Date.now);
  const [failed, setFailed] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const active = useRef<Upload | null>(null);
  const query = useQuery({
    queryKey: ["connector-upload", userId, id],
    queryFn: ({ signal }) => loadConnectorUpload(id, signal),
    enabled: Boolean(connectorOrigin()),
    retry: false,
    gcTime: 0,
  });
  useEffect(
    () => () => {
      void active.current?.abort().catch(reportTransferFailure);
    },
    []
  );
  const upload = query.data;
  useEffect(() => {
    if (!upload) return;
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.max(0, Date.parse(upload.expires_at) - Date.now())
    );
    return () => window.clearTimeout(timer);
  }, [upload]);
  const fail = () => {
    setFailed(true);
    setBusy(false);
    reportTransferFailure();
  };
  const finish = async (cancel = false) => {
    if (!upload) return;
    setBusy(true);
    setSettling(true);
    setFailed(false);
    try {
      await active.current?.abort();
      active.current = null;
      await finishConnectorUpload(upload, cancel);
      await query.refetch();
    } catch {
      fail();
    } finally {
      setBusy(false);
      setSettling(false);
    }
  };
  const start = () => {
    if (!upload || !file || !upload.upload_token) return;
    setBusy(true);
    setFailed(false);
    const transfer = new Upload(file, {
      uploadUrl: transferUrl(upload),
      chunkSize: upload.chunk_size,
      headers: { Authorization: `Bearer ${upload.upload_token}` },
      storeFingerprintForResuming: false,
      retryDelays: [0, 1000, 3000, 5000],
      onProgress: (sent, total) => setProgress(total ? Math.round((sent * 100) / total) : 100),
      onError: fail,
      onSuccess: () => {
        active.current = null;
        void finish();
      },
    });
    active.current = transfer;
    transfer.start();
  };
  const pause = async () => {
    try {
      await active.current?.abort();
      setBusy(false);
    } catch {
      fail();
    }
  };
  return {
    query,
    upload,
    file,
    setFile,
    busy,
    settling,
    now,
    failed,
    setFailed,
    progress,
    finish,
    start,
    pause,
  };
}

function FilePicker({ id }: { id: string }) {
  const { t } = useLanguage();
  const copy = t.connectors;
  const model = useFileTransfer(id);
  const { query, upload } = model;
  const loadState = connectorLoadState(Boolean(connectorOrigin()), query);
  if (loadState || !upload)
    return <ConnectorStatus state={loadState ?? "failed"} retry={() => void query.refetch()} />;

  return (
    <div className="space-y-6">
      <UploadMetadata upload={upload} />
      <p className="text-sm text-muted-foreground">{copy.uploadNotice}</p>
      <UploadControls model={model} upload={upload} />
      <Link
        to="/settings?section=connections"
        className="block rounded-sm text-sm text-primary underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {copy.settings}
      </Link>
    </div>
  );
}

type UploadModel = ReturnType<typeof useFileTransfer>;
type LoadedUpload = NonNullable<UploadModel["upload"]>;

function UploadMetadata({ upload }: { upload: LoadedUpload }) {
  const { t } = useLanguage();
  const copy = t.connectors;
  return (
    <dl className="space-y-3 break-words text-sm">
      <div>
        <dt className="text-muted-foreground">{copy.uploadFile}</dt>
        <dd className="font-medium">{upload.filename}</dd>
        <dd>
          {new Intl.NumberFormat().format(upload.size)} {copy.uploadBytes}
        </dd>
      </div>
      <div>
        <dt className="text-muted-foreground">{copy.uploadDestination}</dt>
        <dd>
          {upload.destination.building_name} / {upload.destination.project_name} /{" "}
          {upload.destination.folder_path || upload.destination.folder_name}
        </dd>
      </div>
    </dl>
  );
}

function UploadControls({ model, upload }: { model: UploadModel; upload: LoadedUpload }) {
  const { t } = useLanguage();
  const copy = t.connectors;
  const { file, setFile, busy, settling, now, failed, setFailed, progress, finish, start, pause } =
    model;
  const valid = file?.name === upload.filename && file.size === upload.size;
  const editable =
    ["pending", "uploaded"].includes(upload.state) && Date.parse(upload.expires_at) > now;
  const percentage =
    progress ?? Math.round(upload.size ? (upload.uploaded * 100) / upload.size : 0);
  return (
    <>
      {!editable ? (
        <p role="status" className="rounded-md bg-muted p-4">
          {upload.state === "completed" ? copy.uploadComplete : copy.uploadClosed}
        </p>
      ) : (
        <>
          <div className="space-y-2">
            <Label htmlFor="connector-file">{copy.uploadChoose}</Label>
            <Input
              id="connector-file"
              type="file"
              disabled={busy}
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                setFailed(false);
              }}
            />
            {file && !valid && (
              <p role="alert" className="text-sm text-destructive">
                {copy.uploadMismatch}
              </p>
            )}
          </div>
          <div className="space-y-2" aria-live="polite">
            <Progress
              aria-label={copy.uploadProgress}
              aria-valuenow={percentage}
              value={percentage}
            />
            <p className="text-sm">{percentage}%</p>
          </div>
          {failed && (
            <Alert variant="destructive">
              <AlertDescription>{copy.uploadFailed}</AlertDescription>
            </Alert>
          )}
          <div className="flex flex-col gap-3 sm:flex-row">
            {busy ? (
              <Button variant="outline" disabled={settling} onClick={() => void pause()}>
                {copy.uploadPause}
              </Button>
            ) : (
              <Button
                disabled={!valid && upload.state !== "uploaded"}
                onClick={() => (upload.state === "uploaded" ? void finish() : start())}
              >
                {copy.uploadStart}
              </Button>
            )}
            <Button variant="outline" disabled={busy} onClick={() => void finish(true)}>
              {copy.uploadCancel}
            </Button>
          </div>
        </>
      )}
    </>
  );
}

export default function ConnectorUpload() {
  const { isAuthenticated, isLoading, userId } = useAuth();
  const { t } = useLanguage();
  const { id = "" } = useParams();
  const location = useLocation();
  if (!isLoading && !isAuthenticated)
    return <Navigate to="/login" state={{ returnUrl: location.pathname }} replace />;
  return (
    <AuthPageLayout contentClassName="max-w-2xl">
      <AuthPageHeader
        title={t.connectors.uploadTitle}
        description={t.connectors.uploadDescription}
        icon={<UploadCloud aria-hidden="true" />}
      />
      {isLoading ? (
        <p role="status">{t.connectors.loading}</p>
      ) : (
        <FilePicker key={`${userId}:${id}`} id={id} />
      )}
    </AuthPageLayout>
  );
}
